/* ============================================================
 * NauticIQ motion driver — Framer Motion parity without React.
 * - html.nq-motion gates all animation CSS (60.nq-motion.css)
 * - IntersectionObserver replaces whileInView reveals
 * - Hero popups open/close on a rotating cadence; agencies tiles/feed still tick here
 * Respects prefers-reduced-motion.
 * ============================================================ */

(function () {
  'use strict';

  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced || !('IntersectionObserver' in window)) return;

  document.documentElement.classList.add('nq-motion');

  function clamp01(n) {
    if (n < 0) return 0;
    if (n > 1) return 1;
    return n;
  }

  // Scroll motion uses translate/scale, not transform, so the
  // data-nq-reveal entrance (which owns transform) still plays.
  // Positions come from offsetTop so the written motion is not measured back in.
  function tagScroll(root, selector, mode) {
    var nodes = root.querySelectorAll(selector);
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].setAttribute('data-nq-scroll', mode);
      nodes[i].style.setProperty('--nq-i', String(i));
    }
  }

  function pageTop(el) {
    var top = 0;
    var node = el;
    while (node) {
      top += node.offsetTop || 0;
      node = node.offsetParent;
    }
    return top;
  }

  function init() {
    var home = document.querySelector('[data-nq-page="home"]');
    var scene = document.querySelector('[data-nq-scene]');
    var scrubEls = [];

    if (home) {
      tagScroll(home, '.nq-section-head', 'headline');
      tagScroll(home, '.nq-benefit-card', 'card');
      tagScroll(home, '.nq-product-card', 'card');
      tagScroll(home, '.nq-ecosystem__hub', 'hub');
      tagScroll(home, '.nq-workflow__step', 'step');
      tagScroll(home, '.nq-integration-card', 'card');
      tagScroll(home, '.nq-trust__stat', 'stat');
      tagScroll(home, '.nq-price-card', 'card');
      tagScroll(home, '.nq-ops__highlight', 'card');
      tagScroll(home, '.nq-final-cta__inner', 'cta');
      tagScroll(home, '.nq-final-cta__bg', 'zoom');
      scrubEls = home.querySelectorAll('[data-nq-scrub], [data-nq-scroll]');
      // Pointer light follows the cursor without moving the card or its text.
      if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        home.querySelectorAll('.nq-benefit-card, .nq-ops__highlight, .nq-integration-card').forEach(function (card) {
          var frame = 0;
          var pointerX = 0;
          var pointerY = 0;
          card.addEventListener('pointermove', function (event) {
            pointerX = event.clientX;
            pointerY = event.clientY;
            if (frame) return;
            frame = window.requestAnimationFrame(function () {
              var box = card.getBoundingClientRect();
              card.style.setProperty('--nq-pointer-x', (pointerX - box.left) + 'px');
              card.style.setProperty('--nq-pointer-y', (pointerY - box.top) + 'px');
              frame = 0;
            });
          }, { passive: true });
        });
      }
    }

    var scrubState = [];
    for (var s = 0; s < scrubEls.length; s++) {
      scrubState.push({ el: scrubEls[s], current: null });
    }

    var sceneCurrent = null;
    var running = false;

    function easeToward(current, target) {
      if (current === null) return target;
      var next = current + (target - current) * 0.14;
      if (Math.abs(next - target) < 0.0015) return target;
      return next;
    }

    function updateScroll() {
      var vh = window.innerHeight || 1;
      var scrollY = window.scrollY || window.pageYOffset || 0;
      var moved = false;

      if (scene) {
        var rect = scene.getBoundingClientRect();
        var distance = scene.offsetHeight - vh;
        var progress;
        if (distance > 32) {
          var stick = Math.min(104, vh * 0.16);
          progress = clamp01((stick - rect.top) / distance);
        } else {
          var slot = scene.querySelector('.nq-hero__mockup-slot');
          var box = slot ? slot.getBoundingClientRect() : rect;
          progress = clamp01((vh * 0.9 - box.top) / (vh * 0.8));
        }
        sceneCurrent = easeToward(sceneCurrent, progress);
        if (sceneCurrent !== progress) moved = true;
        scene.style.setProperty('--nq-p', sceneCurrent.toFixed(4));
      }

      for (var i = 0; i < scrubState.length; i++) {
        var item = scrubState[i];
        var center = pageTop(item.el) + item.el.offsetHeight * 0.5 - scrollY;
        var target = clamp01((vh - center) / (vh * 1.35));
        item.current = easeToward(item.current, target);
        if (item.current !== target) moved = true;
        item.el.style.setProperty('--nq-s', item.current.toFixed(4));
      }

      if (moved) {
        window.requestAnimationFrame(updateScroll);
      } else {
        running = false;
      }
    }

    function requestTick() {
      if (running) return;
      running = true;
      window.requestAnimationFrame(updateScroll);
    }

    window.addEventListener('scroll', requestTick, { passive: true });
    window.addEventListener('resize', requestTick);
    updateScroll();

    // ---------- Scroll reveals (whileInView, once) ----------
    var revealEls = document.querySelectorAll('[data-nq-reveal], [data-nq-fade]');
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var step = parseInt(el.getAttribute('data-nq-delay') || '0', 10);
        el.style.transitionDelay = (step * 0.08).toFixed(2) + 's';
        el.classList.add('nq-inview');
        observer.unobserve(el);
      });
    }, { threshold: 0.15 });

    revealEls.forEach(function (el) {
      observer.observe(el);
    });

    // ---------- Hero: popup cards open / close / loop ----------
    var popupEls = Array.prototype.slice.call(document.querySelectorAll('[data-nq-popup]'));
    if (popupEls.length) {
      var visible = Math.min(3, popupEls.length);
      var queue = [];
      var cursor = 0;

      var setPopup = function (el, state) {
        el.classList.remove('nq-open', 'nq-closing');
        if (state) {
          el.classList.add(state);
        }
      };

      var openNext = function () {
        var guard = 0;
        while (guard < popupEls.length && queue.indexOf(popupEls[cursor]) !== -1) {
          cursor = (cursor + 1) % popupEls.length;
          guard++;
        }
        var el = popupEls[cursor];
        cursor = (cursor + 1) % popupEls.length;
        el.style.left = '';
        el.style.top = '';
        setPopup(el, 'nq-open');
        queue.push(el);
      };

      var closeOldest = function () {
        var el = queue.shift();
        if (!el) return;
        var cs = window.getComputedStyle(el);
        el.style.left = cs.left;
        el.style.top = cs.top;
        el.classList.remove('nq-open');
        el.classList.add('nq-closing');
        setTimeout(function () {
          el.classList.remove('nq-closing');
          el.style.left = '';
          el.style.top = '';
        }, 520);
      };

      for (var p = 0; p < visible; p++) {
        (function (delay) {
          setTimeout(openNext, delay);
        })(450 + p * 420);
      }

      setInterval(function () {
        if (document.hidden || !scene || scene.getBoundingClientRect().bottom < 0) return;
        if (queue.length >= visible) {
          closeOldest();
        }
        setTimeout(openNext, 480);
      }, 3200);
    }

    // ---------- Agencies hero: rotating tile values (2.2s cadence) ----------
    var tileEls = document.querySelectorAll('[data-nq-tile-values]');
    if (tileEls.length) {
      var tileData = [];
      tileEls.forEach(function (el) {
        try {
          tileData.push({ el: el, values: JSON.parse(el.getAttribute('data-nq-tile-values')) || [] });
        } catch (e) {
          /* skip malformed tile */
        }
      });

      var tileTick = 0;
      setInterval(function () {
        tileTick++;
        tileData.forEach(function (tile) {
          if (tile.values.length < 2) return;
          tile.el.classList.add('nq-swap');
          setTimeout(function () {
            tile.el.textContent = tile.values[tileTick % tile.values.length];
            tile.el.classList.remove('nq-swap');
          }, 300);
        });
      }, 2200);
    }

    // ---------- Agencies hero: live marketplace feed (2.6s cadence) ----------
    var feedRow = document.querySelector('[data-nq-feed]');
    if (feedRow) {
      var feed = [];
      try {
        feed = JSON.parse(feedRow.getAttribute('data-nq-feed')) || [];
      } catch (e) {
        feed = [];
      }

      var boatEl = feedRow.querySelector('.nq-ax-feed__boat');
      var statusEl = feedRow.querySelector('.nq-ax-feed__status');

      if (feed.length > 1 && boatEl && statusEl) {
        var feedIndex = 0;
        setInterval(function () {
          feedIndex = (feedIndex + 1) % feed.length;
          feedRow.classList.add('nq-exit');
          setTimeout(function () {
            boatEl.textContent = feed[feedIndex].boat;
            statusEl.textContent = feed[feedIndex].status;
            statusEl.className = 'nq-ax-feed__status ' + (feed[feedIndex].tone || '');
            feedRow.classList.remove('nq-exit');
            feedRow.classList.add('nq-enter');
            void feedRow.offsetWidth;
            feedRow.classList.remove('nq-enter');
          }, 350);
        }, 2600);
      }
    }

  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
